/**
 * TigerCertPicker — a shared dialog for picking a reusable cert out of the Tiger
 * (managed SSL) inventory to distribute onto a service surface (CDN/WAF/WAAP).
 *
 * Only certs that can actually be distributed are listed: `reusable === true`
 * (we hold the key) AND an active/usable status — which mirrors exactly what the
 * backend `GetBundle` accepts. Revoked / awaiting_dns / failed / issuing certs
 * are excluded, since they have no distributable material yet (or ever).
 *
 * Selecting a row calls `onSelect(cert)` and closes; the caller is responsible
 * for the actual attach mutation + toast.
 */
import { useMemo } from 'react';
import { ShieldCheck } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Badge,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { DataTable, StatusPill, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useCerts, type Cert, type CertStatus } from '@/api/tiger';
import { nameCoversHost } from '@/pages/services/waap/waap-util';

/** Statuses whose certs are actually usable for distribution (have material). */
const USABLE: ReadonlySet<CertStatus> = new Set<CertStatus>(['active']);

/**
 * A cert can be picked when it has issued material we can push to an edge:
 * either `reusable` (our own key — internal_le / imported) OR a shared_wildcard
 * owned-zone cert, which is distributable to a vendor edge server-side (the key
 * never reaches the browser — see controlplane Bundle()).
 */
function isDistributable(c: Cert): boolean {
  return (c.reusable === true || c.source === 'shared_wildcard') && USABLE.has(c.status);
}

type Tone = 'success' | 'danger' | 'warning' | 'muted' | 'info' | 'neutral';

function statusTone(s: string): Tone {
  if (s === 'active') return 'success';
  if (s === 'awaiting_dns') return 'info';
  if (s === 'issuing' || s === 'pending') return 'warning';
  if (s === 'failed' || s === 'revoked' || s === 'revoking') return 'danger';
  return 'muted';
}

function statusLabel(s: string): string {
  return s === 'awaiting_dns' ? 'awaiting DNS' : s;
}

function sourceLabel(s: string): string {
  return s === 'internal_le' ? "Let's Encrypt" : s === 'imported' ? 'Uploaded' : s;
}

/** ISO date → `DD/MM/YYYY`; "—" when absent/invalid. */
function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/** Primary domain + "+N more" for the remaining SANs. */
function DomainCell({ names }: { names: string[] }) {
  const primary = names[0] ?? '—';
  const extra = Math.max(0, names.length - 1);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 'var(--vn-font-weight-medium)' }}>
      {primary}
      {extra > 0 && (
        <Badge tone="neutral" appearance="subtle" size="sm">
          +{extra} more
        </Badge>
      )}
    </span>
  );
}

export function TigerCertPicker({
  open,
  onOpenChange,
  onSelect,
  onIssue,
  issuing,
  issueLabel,
  host,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (cert: Cert) => void;
  /** Optional inline "issue a cert" action shown in the empty state, so a caller
   *  with an empty inventory doesn't have to leave for the cert console. */
  onIssue?: () => void;
  issuing?: boolean;
  issueLabel?: string;
  /** When set, only certs whose CN/SAN cover this host are listed — a cert that
   *  doesn't cover the domain can't validly attach here. Omit to list all. */
  host?: string;
}) {
  // Pull a wide page; filter to distributable client-side (the list endpoint has
  // no "reusable" filter, and we want the exact GetBundle-eligible set).
  const { data: certs = [], isLoading, isError, refetch } = useCerts({ limit: 200 });

  const usable = useMemo(() => certs.filter(isDistributable), [certs]);
  // Narrow to certs that actually cover the target host (exact or wildcard).
  const rows = useMemo(
    () => (host ? usable.filter((c) => (c.dns_names ?? []).some((n) => nameCoversHost(n, host))) : usable),
    [usable, host],
  );

  const columns = useMemo<ColumnDef<Cert, unknown>[]>(
    () => [
      {
        id: 'domains',
        header: 'Domain',
        cell: ({ row }) => <DomainCell names={row.original.dns_names ?? []} />,
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusPill tone={statusTone(row.original.status)}>{statusLabel(row.original.status)}</StatusPill>
        ),
      },
      {
        id: 'expiry',
        header: 'Expires',
        cell: ({ row }) => fmtDate(row.original.current_revision?.expires_at),
      },
      {
        id: 'source',
        header: 'Source',
        cell: ({ row }) => (
          <Badge tone="neutral" appearance="subtle" size="sm">
            {sourceLabel(row.original.source)}
          </Badge>
        ),
      },
    ],
    [],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Choose a certificate</DialogTitle>
          <DialogDescription>
            {host
              ? `Pick a reusable certificate that covers ${host}. Only active, distributable certificates are shown.`
              : 'Pick a reusable certificate from your managed inventory to attach to this service. Only active, distributable certificates are shown.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {isLoading ? (
            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 44 }} />
              ))}
            </div>
          ) : isError ? (
            <Alert
              tone="danger"
              title="Couldn't load certificates"
              actions={<Button variant="secondary" onClick={() => refetch()}>Retry</Button>}
            >
              The certificate inventory couldn't be loaded.
            </Alert>
          ) : rows.length === 0 ? (
            <div style={{ display: 'grid', gap: 'var(--vn-space-3)', justifyItems: 'center' }}>
              <EmptyState
                icon={<ShieldCheck size={20} />}
                title={host && usable.length > 0 ? 'No matching certificate' : 'No certificates yet'}
                description={
                  // Certs exist but none cover the host → nudge to issue one for it.
                  host && usable.length > 0
                    ? `None of your ${usable.length} distributable certificate${usable.length === 1 ? '' : 's'} cover ${host}. Issue a free one for this domain.`
                    : onIssue
                      ? 'Issue a free certificate for this domain right here — no need to leave.'
                      : 'Issue a free certificate or upload one in the SSL Certificates inventory, then attach it here.'
                }
              />
              {onIssue && (
                <Button startIcon={<ShieldCheck size={15} />} disabled={issuing} onClick={onIssue}>
                  {issuing ? 'Issuing…' : (issueLabel ?? 'Issue a certificate')}
                </Button>
              )}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={rows}
              getRowId={(r) => r.id}
              onRowClick={(row) => {
                onSelect(row);
                onOpenChange(false);
              }}
              emptyState="No reusable certificates."
              footer={`${rows.length} certificate${rows.length === 1 ? '' : 's'}`}
            />
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TigerCertPicker;
